/**
 * Layout-authoring logic — pure, React-free helpers backing the WYSIWYG
 * direct-manipulation editor on the component config page.
 *
 * The config page lets an admin rename / hide / reorder / tooltip the
 * fields, columns, and sections of a registered component **by direct
 * manipulation**, without writing a sentence. Each affordance emits one of
 * the layout {@link RuleEffect} shapes the engine already understands
 * (`renameField`, `hideField`, `reorderFields`, the column / section
 * equivalents, and the tooltip setters). This module owns:
 *
 * 1. The discoverable {@link LayoutTarget} model (a field / column / section
 *    with a stable `key`, a base label, and the *current* draft override).
 * 2. The reducer-style `applyEdit` that folds a single direct-manipulation
 *    {@link LayoutEdit} into the running list of {@link RuleEffect}s, keeping
 *    one canonical effect per (kind, key, dimension) so re-editing the same
 *    field replaces rather than appends.
 * 3. `buildDraftRule` — wrap the accumulated effects into a synthetic
 *    {@link ComponentRule} with an **always-on** (empty `AND`) condition, so
 *    the live preview can run the real evaluator (`applyRules`) over it and
 *    show the edits WYSIWYG.
 * 4. `buildLayoutRuleJson` — the `{ conditions, effects }` payload persisted
 *    as a tenant-private rule.
 *
 * Kept free of React / fetch / Zod imports so it is unit-testable in
 * isolation and cannot drift from the engine's effect contract (it imports
 * the `RuleEffect` union directly).
 */

import type {
  ComponentRule,
  ConditionGroup,
  RuleEffect,
} from '@components/shared/configurable';
import { DEFAULT_REQUIRED_MESSAGE } from '@components/shared/configurable';
import type {
  ConditionOperator,
  RuleFieldCondition,
} from '@components/integrated/entity-form';

// ---------------------------------------------------------------------------
// Target model
// ---------------------------------------------------------------------------

/**
 * Which declarative surface a target lives on.
 *
 * `'field'` / `'column'` / `'section'` are the original field-set surfaces;
 * `'title'` (the component title) and `'action'` (an EntityForm action button)
 * are config-sourced targets the DOM scrape cannot see — they appear only when
 * the component publishes a base config (see `discoverConfigTargets`). Info-card
 * items reuse the `'field'` kind (they share the rename / hide / reorder /
 * tooltip affordances and have no placeholder/helper).
 *
 * `'filter'` (a DataList toolbar filter input) and `'search'` (the single
 * DataList toolbar search box) are likewise config-sourced. A filter mirrors a
 * column (rename / hide / show / reorder) plus a placeholder; the search target
 * has no label — only a placeholder edit and a hide/show toggle (and there is
 * exactly one per surface, so it is never reordered).
 */
export type LayoutTargetKind =
  | 'field'
  | 'column'
  | 'section'
  | 'title'
  | 'action'
  | 'filter'
  | 'search';

/**
 * One direct-manipulation target the panel can edit: a field, column, section,
 * title, or action button — sourced either from the component's **published
 * base config** (`discoverConfigTargets`, the reliable route) or scraped from
 * the live-preview DOM (`discoverLayoutTargets`, the fallback).
 *
 * `key` is the **stable** contract a rule points at (the field name, column
 * key, section id, the literal `'__title__'`, or an action key) and never
 * changes; `baseLabel` is the label as the component renders it today (used as
 * the placeholder / reset value). The panel computes the *effective* label by
 * overlaying any draft rename effect on top of `baseLabel`.
 *
 * The `base*` fields below are populated only by the config-sourced path (the
 * DOM scrape leaves them `undefined`, since it cannot reliably read a
 * placeholder apart from a label, a hidden field's default, etc.). The
 * `supports*` flags tell the panel which editable inputs to render for this
 * target (e.g. a checkbox field has no placeholder).
 */
export interface LayoutTarget {
  kind: LayoutTargetKind;
  /**
   * Stable key — the field name / column key / section id, the literal
   * `'__title__'` for a title target, or the action key (`'submit'` /
   * `'cancel'` / `'reset'` / a custom id) for an action target.
   */
  key: string;
  /** Label as rendered today, shown as the editable default. */
  baseLabel: string;
  /**
   * Default placeholder (field/column), config-sourced. `undefined` when the
   * surface has no placeholder or in the DOM-fallback path (which cannot
   * separate placeholder from label).
   */
  basePlaceholder?: string;
  /** Default helper text (field), config-sourced. `undefined` when none. */
  baseHelperText?: string;
  /**
   * Default tooltip (field/column header), config-sourced, so the editor can
   * show it and offer an explicit clear. `undefined` when none.
   */
  baseTooltip?: string;
  /**
   * Default visibility — `true` when the field/column is hidden in the base
   * config. Lets the editor keep a hidden target **listed** (greyed, toggle
   * off) instead of dropping it (req #6). `undefined`/`false` ⇒ visible.
   */
  baseHidden?: boolean;
  /**
   * Default client-side **required** state — `true` when the field declares
   * `validation.required` in the base config. The editor reads this so the
   * front-end-required toggle reflects the baseline. `undefined`/`false` ⇒ not
   * required by default.
   */
  baseRequired?: boolean;
  /**
   * Whether this target supports a placeholder input. `true` for text-like
   * fields (text/number/email/password/tel/url/textarea/select); `false` for
   * checkbox/date/file/section/title/action and for columns.
   */
  supportsPlaceholder?: boolean;
  /** Whether this target supports a helper-text input (all non-section fields). */
  supportsHelperText?: boolean;
  /** Whether this target supports a tooltip (fields, columns, info items). */
  supportsTooltip?: boolean;
  /**
   * Whether this target supports the **front-end-required** toggle. Add-only:
   * `true` ONLY for fields that are NOT baseline-required (a baseline-required
   * field gets `false`, so no toggle is offered — we never relax it).
   */
  supportsRequired?: boolean;
  /**
   * Whether the field-condition editor offers the value-setting "Cocher" /
   * "Décocher" effects (`setFieldCondition` `effect.checked`) for this target.
   * `true` only for single-boolean `checkbox` fields (config-sourced). A
   * `checkboxGroup` holds a string array, not a boolean, so it does not
   * qualify; DOM-fallback targets carry no field type and never qualify.
   */
  supportsCheckedCondition?: boolean;
}

/**
 * One **data row** the panel can drag-reorder (data-row-ordering Tier 2). Rows
 * are records, not layout config, so they are modelled separately from
 * {@link LayoutTarget}: `id` is the **stable** business id a `reorderRows`
 * effect points at; `label` is a human-friendly preview (e.g. the row's first
 * meaningful cell) shown next to the grip. The panel only renders these when
 * the surface supports manual ordering (inline `data[]` + a stable id).
 */
export interface RowTarget {
  /** Stable row id — the contract `reorderRows.order` lists. */
  id: string;
  /** Display label for the row (preview only; never targeted). */
  label: string;
}

// ---------------------------------------------------------------------------
// Edit model (one user gesture → one edit)
// ---------------------------------------------------------------------------

/**
 * A single direct-manipulation gesture. The panel converts a user action
 * (typing a new label, flicking a switch, dragging a row, editing a tooltip)
 * into one of these, then folds it into the effect list with {@link applyEdit}.
 */
export type LayoutEdit =
  | { kind: 'field'; op: 'rename'; key: string; label: string }
  | {
      kind: 'field';
      op: 'hide';
      key: string;
      /** Desired visibility: `true` ⇒ hide the field, `false` ⇒ show it. */
      hidden: boolean;
      /**
       * The field's **base** (config) visibility, so the reducer can pick the
       * minimal effect: a desired state matching the base removes any draft
       * show/hide slot (no redundant effect); a desired state differing from
       * the base emits the corresponding `hideField` / `showField`. Defaults to
       * base-visible when omitted (the DOM-fallback path has no base info).
       */
      baseHidden?: boolean;
    }
  | { kind: 'field'; op: 'tooltip'; key: string; tooltip: string }
  | { kind: 'field'; op: 'placeholder'; key: string; placeholder: string }
  | { kind: 'field'; op: 'helperText'; key: string; helperText: string }
  | { kind: 'field'; op: 'clearTooltip'; key: string }
  /**
   * Mark a field **client-side required** (front-end only). Add-only: `required:
   * true` stages a `requireField` effect (with an optional custom `message`);
   * `required: false` removes the staged effect (there is no "unrequire"
   * effect). Only offered for fields that are NOT baseline-required.
   */
  | {
      kind: 'field';
      op: 'require';
      key: string;
      required: boolean;
      message?: string;
    }
  /**
   * Stage a **runtime form-value condition** on a field: hide / disable /
   * require it depending on the *live* value of ANOTHER field of the same
   * component (`condition.when`), emitting a `setFieldCondition` effect.
   *
   * Append-only, unlike every other field op: several conditions may target
   * one field key and apply in effect order (last matching wins), so a second
   * add does NOT replace the first — only a byte-identical duplicate collapses
   * (see the `setFieldCondition` arm of {@link effectSlot}).
   */
  | {
      kind: 'field';
      op: 'addCondition';
      key: string;
      condition: RuleFieldCondition;
    }
  /**
   * Drop the `index`-th staged condition of `key` — the position among *that
   * key's* `setFieldCondition` effects in `effects` array order, exactly the
   * numbering {@link effectiveConditions} reports. Positional rather than
   * slot-based (several conditions share one key), so it emits no effect and
   * is handled by an explicit branch in {@link applyEdit}.
   */
  | { kind: 'field'; op: 'removeCondition'; key: string; index: number }
  | { kind: 'field'; op: 'reorder'; order: string[] }
  | { kind: 'column'; op: 'rename'; key: string; label: string }
  | {
      kind: 'column';
      op: 'hide';
      key: string;
      /** Desired visibility: `true` ⇒ hide the column, `false` ⇒ show it. */
      hidden: boolean;
      /** The column's **base** (config) visibility — see field/hide above. */
      baseHidden?: boolean;
    }
  | {
      kind: 'column';
      op: 'tooltip';
      key: string;
      tooltip: string;
      target?: 'header' | 'cell';
    }
  | {
      kind: 'column';
      op: 'clearTooltip';
      key: string;
      target?: 'header' | 'cell';
    }
  | { kind: 'column'; op: 'reorder'; order: string[] }
  | { kind: 'section'; op: 'reorder'; order: string[] }
  /**
   * Set / clear the component-level title (config-sourced target). A blank
   * `title` removes the override (back to the component's default title).
   */
  | { kind: 'title'; op: 'rename'; title: string }
  /** Rename an action button (submit/cancel/reset/custom). Blank removes it. */
  | { kind: 'action'; op: 'rename'; key: string; label: string }
  /**
   * Toggle an action button's visibility. `hidden: true` hides it;
   * `hidden: false` removes the hide override (back to the default visible).
   */
  | { kind: 'action'; op: 'hide'; key: string; hidden: boolean }
  /**
   * Drag-reorder of **data rows** (data-row-ordering Tier 2). Unlike the
   * field/column/section reorders this targets the *records* — `order` lists
   * the rows' **stable ids** in the desired sequence and emits a `reorderRows`
   * effect. Only authorable for surfaces that support it (a DataList over a
   * fully-loaded inline `data[]` with a stable row id); the panel gates the
   * gesture and disables it with a message otherwise.
   */
  | { kind: 'row'; op: 'reorder'; order: string[] }
  // --- DataList toolbar filters (mirror the column arms, keyed by filter id) ---
  | { kind: 'filter'; op: 'rename'; key: string; label: string }
  | {
      kind: 'filter';
      op: 'hide';
      key: string;
      /** Desired visibility: `true` ⇒ hide the filter, `false` ⇒ show it. */
      hidden: boolean;
      /** The filter's **base** (config) visibility — see field/hide above. */
      baseHidden?: boolean;
    }
  | { kind: 'filter'; op: 'placeholder'; key: string; placeholder: string }
  | { kind: 'filter'; op: 'reorder'; order: string[] }
  // --- DataList toolbar search (single input — placeholder + hide, no rename) ---
  | { kind: 'search'; op: 'placeholder'; placeholder: string }
  | {
      kind: 'search';
      op: 'hide';
      /** Desired visibility: `true` ⇒ hide search, `false` ⇒ show it. */
      hidden: boolean;
      /** The search's **base** (config) visibility — see field/hide above. */
      baseHidden?: boolean;
    };

// ---------------------------------------------------------------------------
// Effect construction (one edit → the engine effect it emits)
// ---------------------------------------------------------------------------

/**
 * Translate a {@link LayoutEdit} into the {@link RuleEffect} the engine
 * consumes. `hide` with `hidden: false` is the "show again" gesture; it
 * removes the hide effect rather than emitting one (handled in
 * {@link applyEdit}), so this returns `null` for that case.
 *
 * Exported so the panel and the tests can assert the exact effect object a
 * gesture produces without going through the whole list reducer.
 */
export function editToEffect(edit: LayoutEdit): RuleEffect | null {
  switch (edit.kind) {
    case 'field':
      switch (edit.op) {
        case 'rename':
          return { type: 'renameField', key: edit.key, label: edit.label };
        case 'hide':
          // Emit the positive/negative visibility effect only when the desired
          // state DIFFERS from the base; when it matches the base the reducer
          // removes the slot (no redundant effect). See `applyEdit`.
          if (edit.hidden) {
            return (edit.baseHidden ?? false)
              ? null
              : { type: 'hideField', key: edit.key };
          }
          return (edit.baseHidden ?? false)
            ? { type: 'showField', key: edit.key }
            : null;
        case 'tooltip':
          return {
            type: 'setFieldTooltip',
            key: edit.key,
            tooltip: edit.tooltip,
          };
        case 'placeholder':
          return {
            type: 'setFieldPlaceholder',
            key: edit.key,
            placeholder: edit.placeholder,
          };
        case 'helperText':
          return {
            type: 'setFieldHelperText',
            key: edit.key,
            helperText: edit.helperText,
          };
        case 'clearTooltip':
          return { type: 'clearFieldTooltip', key: edit.key };
        case 'require':
          // Add-only: emit a `requireField` effect ONLY when turning required
          // ON (the control is shown only for fields that are non-required at
          // base, so there is nothing to diff against). Turning it OFF emits no
          // effect — the reducer's removal slot drops the staged require. The
          // message defaults to `DEFAULT_REQUIRED_MESSAGE` when blank so the
          // default text lives in exactly one place.
          if (!edit.required) return null;
          return {
            type: 'requireField',
            key: edit.key,
            message:
              edit.message !== undefined && edit.message.trim() !== ''
                ? edit.message
                : DEFAULT_REQUIRED_MESSAGE,
          };
        case 'addCondition':
          // `value` stays ABSENT when the condition omits it (`isEmpty` /
          // `isNotEmpty` carry no comparison value), so the emitted effect is
          // byte-identical to a hand-written / NL-translated one.
          return {
            type: 'setFieldCondition',
            key: edit.key,
            when: edit.condition.when,
            operator: edit.condition.operator,
            ...(edit.condition.value !== undefined
              ? { value: edit.condition.value }
              : {}),
            effect: edit.condition.effect,
          };
        case 'removeCondition':
          // Not an emitter: there is no "un-condition" effect. `applyEdit`
          // removes the indexed `setFieldCondition` directly.
          return null;
        case 'reorder':
          return { type: 'reorderFields', order: edit.order };
      }
      return null;
    case 'column':
      switch (edit.op) {
        case 'rename':
          return { type: 'renameColumn', key: edit.key, label: edit.label };
        case 'hide':
          // Symmetric to the field/hide case above.
          if (edit.hidden) {
            return (edit.baseHidden ?? false)
              ? null
              : { type: 'hideColumn', key: edit.key };
          }
          return (edit.baseHidden ?? false)
            ? { type: 'showColumn', key: edit.key }
            : null;
        case 'tooltip':
          return {
            type: 'setColumnTooltip',
            key: edit.key,
            tooltip: edit.tooltip,
            ...(edit.target !== undefined ? { target: edit.target } : {}),
          };
        case 'clearTooltip':
          return {
            type: 'clearColumnTooltip',
            key: edit.key,
            ...(edit.target !== undefined ? { target: edit.target } : {}),
          };
        case 'reorder':
          return { type: 'reorderColumns', order: edit.order };
      }
      return null;
    case 'section':
      // Only reorder is authorable for sections (matches the effect union).
      return { type: 'reorderSections', order: edit.order };
    case 'title':
      // Only rename is authorable for the title; a blank value removes the
      // override (handled in applyEdit) but still produces the effect object.
      return { type: 'setTitle', title: edit.title };
    case 'action':
      switch (edit.op) {
        case 'rename':
          return {
            type: 'setActionLabel',
            action: edit.key,
            label: edit.label,
          };
        case 'hide':
          // `hidden: false` is the "show again" gesture — it removes the
          // hideAction effect rather than emitting one (handled in applyEdit).
          return edit.hidden ? { type: 'hideAction', action: edit.key } : null;
      }
      return null;
    case 'row':
      // Only reorder is authorable for rows — `order` is the stable row ids.
      return { type: 'reorderRows', order: edit.order };
    case 'filter':
      switch (edit.op) {
        case 'rename':
          return { type: 'renameFilter', key: edit.key, label: edit.label };
        case 'hide':
          // Symmetric to the field/column hide cases above.
          if (edit.hidden) {
            return (edit.baseHidden ?? false)
              ? null
              : { type: 'hideFilter', key: edit.key };
          }
          return (edit.baseHidden ?? false)
            ? { type: 'showFilter', key: edit.key }
            : null;
        case 'placeholder':
          return {
            type: 'setFilterPlaceholder',
            key: edit.key,
            placeholder: edit.placeholder,
          };
        case 'reorder':
          return { type: 'reorderFilters', order: edit.order };
      }
      return null;
    case 'search':
      switch (edit.op) {
        case 'placeholder':
          return { type: 'setSearchPlaceholder', placeholder: edit.placeholder };
        case 'hide':
          // The search box has both hide/show effects (like a field/column), so
          // emit the positive/negative effect only when the desired state
          // DIFFERS from the base; matching the base removes the slot.
          if (edit.hidden) {
            return (edit.baseHidden ?? false) ? null : { type: 'hideSearch' };
          }
          return (edit.baseHidden ?? false) ? { type: 'showSearch' } : null;
      }
      return null;
  }
}

// ---------------------------------------------------------------------------
// Effect-list reducer
// ---------------------------------------------------------------------------

/**
 * Identity key for "this effect occupies the same slot as that one" — so a
 * second rename of the same field replaces the first instead of stacking.
 * The slot is (effect type + the key it targets); reorder effects have no
 * per-key slot, so they collapse on their type alone (one reorder per
 * surface).
 */
function effectSlot(effect: RuleEffect): string {
  switch (effect.type) {
    case 'renameField':
    case 'setFieldTooltip':
    case 'setFieldPlaceholder':
    case 'setFieldHelperText':
    case 'clearFieldTooltip':
    case 'renameColumn':
    case 'renameFilter':
    case 'setFilterPlaceholder':
      return `${effect.type}:${effect.key}`;
    // One `requireField` per field — re-toggling required (or editing its
    // message) replaces the prior require effect for that key.
    case 'requireField':
      return `requiredField:${effect.key}`;
    // `showField`/`hideField` (and the column pair) are mutually exclusive —
    // one visibility effect per field/column — so they share a single slot
    // keyed on the field/column key, NOT on the effect type. Setting one
    // visibility intent therefore replaces the other.
    case 'hideField':
    case 'showField':
      return `fieldVisibility:${effect.key}`;
    case 'hideColumn':
    case 'showColumn':
      return `columnVisibility:${effect.key}`;
    // The filter hide/show pair shares one slot per filter id — one visibility
    // intent per filter — mirroring the field/column visibility slots.
    case 'hideFilter':
    case 'showFilter':
      return `filterVisibility:${effect.key}`;
    // The search box is a singleton — one hide/show across the whole surface.
    case 'hideSearch':
    case 'showSearch':
      return 'searchVisibility';
    // One search placeholder per surface (search is a singleton).
    case 'setSearchPlaceholder':
      return 'setSearchPlaceholder';
    case 'setColumnTooltip':
    case 'clearColumnTooltip':
      return `${effect.type}:${effect.key}:${effect.target ?? 'header'}`;
    case 'setTitle':
      // One title per component.
      return 'setTitle';
    case 'setActionLabel':
    case 'hideAction':
      return `${effect.type}:${effect.action}`;
    case 'reorderFields':
    case 'reorderColumns':
    case 'reorderSections':
    case 'reorderRows':
    case 'reorderFilters':
      return effect.type;
    // `setFieldCondition` is the ONE append-only arm: several runtime
    // conditions may target the same field and apply in effect order (last
    // matching wins). Slotting it on the bare key would make a second
    // condition silently replace the first, so the slot carries the whole
    // condition payload — distinct conditions on one key coexist, while
    // staging the *same* condition twice is idempotent (an identical duplicate
    // replaces itself instead of stacking a no-op copy).
    case 'setFieldCondition':
      return `setFieldCondition:${effect.key}:${JSON.stringify({
        when: effect.when,
        operator: effect.operator,
        value: effect.value,
        effect: effect.effect,
      })}`;
    default:
      // Non-layout effects are never produced here, but keep the function
      // total: treat each as its own unique slot so nothing is dropped.
      return JSON.stringify(effect);
  }
}

/**
 * The slot a "remove" gesture targets when it emits no effect.
 *
 * A visibility toggle whose desired state matches the field/column's **base**
 * visibility removes any draft show/hide slot for that key (back to base, with
 * no redundant effect). An action "show again" removes its `hideAction` slot.
 * The visibility slots are shared between the hide/show pair, so removing
 * either polarity clears the other too (one visibility effect per target).
 * Turning the add-only front-end-required toggle OFF removes its
 * `requireField` slot (back to the non-required baseline).
 */
function removalSlot(edit: LayoutEdit): string | null {
  if (edit.kind === 'field' && edit.op === 'hide') {
    // Desired ON matching base-visible, or desired OFF matching base-hidden,
    // means "no override" ⇒ drop the visibility slot. `editToEffect` returns
    // null in exactly those cases, so we mirror that condition here.
    const desiredMatchesBase = edit.hidden === (edit.baseHidden ?? false);
    return desiredMatchesBase ? `fieldVisibility:${edit.key}` : null;
  }
  if (edit.kind === 'column' && edit.op === 'hide') {
    const desiredMatchesBase = edit.hidden === (edit.baseHidden ?? false);
    return desiredMatchesBase ? `columnVisibility:${edit.key}` : null;
  }
  if (edit.kind === 'filter' && edit.op === 'hide') {
    const desiredMatchesBase = edit.hidden === (edit.baseHidden ?? false);
    return desiredMatchesBase ? `filterVisibility:${edit.key}` : null;
  }
  if (edit.kind === 'search' && edit.op === 'hide') {
    const desiredMatchesBase = edit.hidden === (edit.baseHidden ?? false);
    return desiredMatchesBase ? 'searchVisibility' : null;
  }
  if (edit.kind === 'action' && edit.op === 'hide' && !edit.hidden) {
    return `hideAction:${edit.key}`;
  }
  // Turning the add-only required toggle OFF removes the staged `requireField`
  // slot (back to the field's non-required baseline, no residue). `editToEffect`
  // returns null in exactly this case, so we mirror that condition here.
  if (edit.kind === 'field' && edit.op === 'require' && !edit.required) {
    return `requiredField:${edit.key}`;
  }
  return null;
}

/**
 * Fold a single {@link LayoutEdit} into the running list of effects.
 *
 * - Re-editing the same dimension of the same target **replaces** the prior
 *   effect (one canonical effect per slot) so the list stays minimal and the
 *   preview is deterministic.
 * - A visibility toggle whose desired state matches the base, "show again", a
 *   blank rename / placeholder / helper / title / action-label, and a blank
 *   tooltip **remove** the corresponding effect rather than emitting an empty
 *   one, so resetting a target to its component default leaves no rule residue.
 * - A `clearTooltip` op emits the dedicated `clear*Tooltip` effect AND drops
 *   any prior `set*Tooltip` slot for the same key, so a set + clear can never
 *   coexist (the explicit clear always wins).
 * - A reorder whose order matches the natural order can still be emitted; the
 *   panel decides when to call this. The reducer does not second-guess it.
 *
 * Returns a **new** array; never mutates the input (referential-friendly for
 * React state setters).
 */
export function applyEdit(
  effects: readonly RuleEffect[],
  edit: LayoutEdit,
): RuleEffect[] {
  // Removing a staged runtime condition is POSITIONAL, not slot-based (several
  // `setFieldCondition`s legitimately share one field key), so it cannot go
  // through the slot machinery: drop the `index`-th effect among that key's
  // conditions and leave every other effect — including the key's other
  // conditions — untouched.
  if (edit.kind === 'field' && edit.op === 'removeCondition') {
    let seen = -1;
    return effects.filter((e) => {
      if (e.type !== 'setFieldCondition' || e.key !== edit.key) return true;
      seen += 1;
      return seen !== edit.index;
    });
  }

  const next = editToEffect(edit);

  // A "remove" gesture (visibility back to base, action show-again) deletes
  // the slot.
  const removeSlot = removalSlot(edit);
  if (removeSlot !== null) {
    return effects.filter((e) => effectSlot(e) !== removeSlot);
  }

  // A blank label / placeholder / helper / title / action-label clears the
  // override (back to component default), mirroring the blank-rename behaviour.
  if (next !== null && isBlankRemovalEffect(next)) {
    const slot = effectSlot(next);
    return effects.filter((e) => effectSlot(e) !== slot);
  }
  // A blank tooltip (soft clear via typing) drops the tooltip slot. The
  // explicit "remove tooltip" button uses the `clearTooltip` op below instead.
  if (
    next !== null &&
    (next.type === 'setFieldTooltip' || next.type === 'setColumnTooltip') &&
    next.tooltip.trim() === ''
  ) {
    const slot = effectSlot(next);
    return effects.filter((e) => effectSlot(e) !== slot);
  }

  if (next === null) return [...effects];

  // A `clearTooltip` op must also remove the matching `set*Tooltip` slot so the
  // explicit clear can't coexist with a prior set on the same key/target.
  if (next.type === 'clearFieldTooltip' || next.type === 'clearColumnTooltip') {
    const setSlot =
      next.type === 'clearFieldTooltip'
        ? `setFieldTooltip:${next.key}`
        : `setColumnTooltip:${next.key}:${next.target ?? 'header'}`;
    const clearSlot = effectSlot(next);
    const withoutBoth = effects.filter(
      (e) => effectSlot(e) !== setSlot && effectSlot(e) !== clearSlot,
    );
    withoutBoth.push(next);
    return withoutBoth;
  }

  const slot = effectSlot(next);
  const withoutSlot = effects.filter((e) => effectSlot(e) !== slot);
  withoutSlot.push(next);
  return withoutSlot;
}

/**
 * Whether a freshly-produced effect represents a **blank** text override that
 * should remove its slot rather than persist an empty value (so reverting a
 * label / placeholder / helper / title / action-label to blank reverts to the
 * component default). Tooltips are handled separately (dedicated clear arms).
 */
function isBlankRemovalEffect(effect: RuleEffect): boolean {
  switch (effect.type) {
    case 'renameField':
    case 'renameColumn':
    case 'setActionLabel':
      return effect.label.trim() === '';
    case 'setFieldPlaceholder':
    case 'setFilterPlaceholder':
    case 'setSearchPlaceholder':
      return effect.placeholder.trim() === '';
    case 'setFieldHelperText':
      return effect.helperText.trim() === '';
    case 'setTitle':
      return effect.title.trim() === '';
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Runtime field-condition value coercion
// ---------------------------------------------------------------------------

/**
 * Operators whose predicate takes NO comparison value — a
 * {@link RuleFieldCondition} built on one of these omits `value` entirely.
 */
export const VALUELESS_OPERATORS: ReadonlySet<ConditionOperator> =
  new Set<ConditionOperator>(['isEmpty', 'isNotEmpty']);

/**
 * Operators whose value is a **list** — the authoring UI takes it as a
 * comma-separated string and {@link parseConditionValue} splits it.
 */
export const LIST_OPERATORS: ReadonlySet<ConditionOperator> =
  new Set<ConditionOperator>(['in', 'notIn']);

/**
 * Coerce the raw text an admin typed in the Condition panel into the `value` a
 * {@link RuleFieldCondition} carries, per the chosen operator:
 *
 * - `isEmpty` / `isNotEmpty` → `undefined`, so the effect omits `value`;
 * - `in` / `notIn` → the comma-separated list, trimmed, empties dropped
 *   (`string[]`, never coerced further — a list stays a list of strings);
 * - the literal `true` / `false` → the boolean, so a **checkbox** source field
 *   (whose form value is a boolean) compares correctly under the evaluator's
 *   strict `===`;
 * - anything else → the trimmed string.
 *
 * Lives here rather than in the panel so it is React-free and unit-testable in
 * isolation, like every other authoring primitive in this module.
 */
export function parseConditionValue(
  raw: string,
  operator: ConditionOperator,
): unknown {
  if (VALUELESS_OPERATORS.has(operator)) return undefined;
  if (LIST_OPERATORS.has(operator)) {
    return raw
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part !== '');
  }
  const trimmed = raw.trim();
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  return trimmed;
}

// ---------------------------------------------------------------------------
// Draft rule (always-on) for the live preview + persistence
// ---------------------------------------------------------------------------

/** A vacuously-true condition group — an empty AND is "always fire". */
export const ALWAYS_ON_CONDITION: ConditionGroup = {
  operator: 'AND',
  conditions: [],
};

/**
 * Wrap the accumulated layout effects in a synthetic, **active**
 * {@link ComponentRule} so the live preview can run the real engine
 * (`applyRules`) over it and reflect the edits WYSIWYG. The condition is
 * always-on (empty AND), matching how the saved rule will behave when no NL
 * condition has been added.
 *
 * The synthetic id (`__draft__`) never reaches the backend — it exists only
 * so the evaluator's stable-sort tie-break has something to read.
 */
export function buildDraftRule(
  componentId: string,
  effects: readonly RuleEffect[],
  conditions: ConditionGroup = ALWAYS_ON_CONDITION,
): ComponentRule {
  return {
    id: '__draft__',
    componentId,
    name: 'Draft layout rule',
    description: '',
    nlInput: '',
    conditions,
    effects: [...effects],
    status: 'active',
    priority: 0,
  };
}

/**
 * Build the `{ conditions, effects }` `rule_json` body persisted for a layout
 * rule. Defaults to the always-on condition; the NL path can pass a richer
 * condition group for *conditional* layout rules.
 */
export function buildLayoutRuleJson(
  effects: readonly RuleEffect[],
  conditions: ConditionGroup = ALWAYS_ON_CONDITION,
): { conditions: ConditionGroup; effects: RuleEffect[] } {
  return { conditions, effects: [...effects] };
}

// ---------------------------------------------------------------------------
// Arrow reorder reducer (replaces drag-and-drop)
// ---------------------------------------------------------------------------

/**
 * Move `key` one position towards the start (`'up'`) or end (`'down'`) of
 * `orderedKeys`, swapping it with its immediate neighbour, and return the
 * **full new key order**. A no-op (returns a copy of the input order) when
 * `key` is missing or already at the boundary in the requested direction.
 *
 * Pure — the panel's Up/Down arrow buttons call this to build the order the
 * `reorder*` effect carries (the engine contract is unchanged from the old
 * drag mechanic; only the gesture differs).
 */
export function reorderByArrow(
  orderedKeys: readonly string[],
  key: string,
  dir: 'up' | 'down',
): string[] {
  const order = [...orderedKeys];
  const index = order.indexOf(key);
  if (index === -1) return order;
  const swapWith = dir === 'up' ? index - 1 : index + 1;
  if (swapWith < 0 || swapWith >= order.length) return order;
  const current = order[index];
  const neighbour = order[swapWith];
  if (current === undefined || neighbour === undefined) return order;
  order[index] = neighbour;
  order[swapWith] = current;
  return order;
}

// ---------------------------------------------------------------------------
// Effect → current override readers (so the panel can show live values)
// ---------------------------------------------------------------------------

/**
 * Resolve the *effective* label for a target given the draft effects: the
 * latest matching rename effect's label, or the target's base label when no
 * rename is in effect.
 */
export function effectiveLabel(
  target: LayoutTarget,
  effects: readonly RuleEffect[],
): string {
  const renameType =
    target.kind === 'column'
      ? 'renameColumn'
      : target.kind === 'filter'
        ? 'renameFilter'
        : 'renameField';
  for (let i = effects.length - 1; i >= 0; i -= 1) {
    const e = effects[i];
    if (e === undefined) continue;
    if (
      (e.type === 'renameColumn' ||
        e.type === 'renameField' ||
        e.type === 'renameFilter') &&
      e.type === renameType &&
      e.key === target.key
    ) {
      return e.label;
    }
  }
  return target.baseLabel;
}

/**
 * The target's **effective** visibility given the draft effects: the latest
 * draft show/hide effect for its key wins (`hideField`/`hideColumn` ⇒ hidden,
 * `showField`/`showColumn` ⇒ visible); with no draft visibility effect it falls
 * back to the target's `baseHidden` (config-sourced) default. This lets a
 * baseline-hidden field/column toggle back on (req #6) and reflects the
 * baseline state even before any edit.
 */
export function isHidden(
  target: LayoutTarget,
  effects: readonly RuleEffect[],
): boolean {
  const hideType =
    target.kind === 'column'
      ? 'hideColumn'
      : target.kind === 'filter'
        ? 'hideFilter'
        : 'hideField';
  const showType =
    target.kind === 'column'
      ? 'showColumn'
      : target.kind === 'filter'
        ? 'showFilter'
        : 'showField';
  for (let i = effects.length - 1; i >= 0; i -= 1) {
    const e = effects[i];
    if (e === undefined) continue;
    if (
      (e.type === 'hideColumn' ||
        e.type === 'hideField' ||
        e.type === 'hideFilter') &&
      e.type === hideType &&
      e.key === target.key
    ) {
      return true;
    }
    if (
      (e.type === 'showColumn' ||
        e.type === 'showField' ||
        e.type === 'showFilter') &&
      e.type === showType &&
      e.key === target.key
    ) {
      return false;
    }
  }
  return target.baseHidden ?? false;
}

/**
 * True when an action button is currently hidden by a draft `hideAction`
 * effect. (Action targets seed their toggle from `target.baseHidden`; this
 * reader reports only the *draft* state the panel's switch reflects.)
 */
export function isActionHidden(
  key: string,
  effects: readonly RuleEffect[],
): boolean {
  return effects.some((e) => e.type === 'hideAction' && e.action === key);
}

/**
 * The toolbar search box's **effective** visibility given the draft effects:
 * the latest draft `hideSearch` / `showSearch` wins (`hideSearch` ⇒ hidden,
 * `showSearch` ⇒ visible); with no draft visibility effect it falls back to the
 * supplied `baseHidden` (config-sourced) default. Mirrors {@link isHidden} but
 * for the singleton search input (no key — there is one search box per surface),
 * so a baseline-hidden search box can toggle back on.
 */
export function isSearchHidden(
  baseHidden: boolean,
  effects: readonly RuleEffect[],
): boolean {
  for (let i = effects.length - 1; i >= 0; i -= 1) {
    const e = effects[i];
    if (e === undefined) continue;
    if (e.type === 'hideSearch') return true;
    if (e.type === 'showSearch') return false;
  }
  return baseHidden;
}

/**
 * Resolve the **effective** order of data rows given the draft effects: the
 * latest `reorderRows` effect re-sequences `rows` exactly as the engine's
 * `applyRowOrder` will (listed ids first in the effect's order, unlisted rows
 * kept in natural order after, ids not present ignored, new ids appended), so
 * the authoring panel previews the same order the saved rule produces. With no
 * `reorderRows` effect the rows are returned in their natural (input) order.
 *
 * Returns the rows themselves (not just ids) so the panel can render labels.
 */
export function effectiveRowOrder(
  rows: readonly RowTarget[],
  effects: readonly RuleEffect[],
): RowTarget[] {
  let order: readonly string[] | undefined;
  for (let i = effects.length - 1; i >= 0; i -= 1) {
    const e = effects[i];
    if (e !== undefined && e.type === 'reorderRows') {
      order = e.order;
      break;
    }
  }
  if (order === undefined || order.length === 0) return [...rows];

  const rank = new Map<string, number>();
  for (let i = 0; i < order.length; i += 1) {
    const id = order[i];
    if (id !== undefined && !rank.has(id)) rank.set(id, i);
  }

  const listed: Array<{ row: RowTarget; rank: number; index: number }> = [];
  const rest: RowTarget[] = [];
  rows.forEach((row, index) => {
    const r = rank.get(row.id);
    if (r === undefined) rest.push(row);
    else listed.push({ row, rank: r, index });
  });
  if (listed.length === 0) return [...rows];

  listed.sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : a.index - b.index));
  return [...listed.map((entry) => entry.row), ...rest];
}

/**
 * Resolve the **effective** order of same-kind layout targets (fields /
 * columns / sections / filters) given the draft effects — the
 * field/column/section/filter analogue of {@link effectiveRowOrder}, keyed on
 * each `target.key` instead of a row id. The latest matching reorder effect
 * (`reorderFields` for `'field'`, `reorderColumns` for `'column'`,
 * `reorderSections` for `'section'`, `reorderFilters` for `'filter'`)
 * re-sequences `targets` exactly as the engine's order resolution will: listed
 * keys first in the effect's order, unlisted targets kept in natural (input)
 * order after, keys not present among the targets ignored. A reorder effect of
 * the *wrong* kind is skipped. With no matching reorder effect the targets are
 * returned in their natural order.
 *
 * Returns the targets themselves (not just keys) so the panel can render them
 * directly, and a fresh array (never the input reference). This is what keeps
 * the editor's Fields/Columns/Sections list in lock-step with the live preview
 * after a committed reorder.
 */
export function effectiveTargetOrder(
  targets: readonly LayoutTarget[],
  kind: 'field' | 'column' | 'section' | 'filter',
  effects: readonly RuleEffect[],
): LayoutTarget[] {
  const reorderType =
    kind === 'field'
      ? 'reorderFields'
      : kind === 'column'
        ? 'reorderColumns'
        : kind === 'filter'
          ? 'reorderFilters'
          : 'reorderSections';

  let order: readonly string[] | undefined;
  for (let i = effects.length - 1; i >= 0; i -= 1) {
    const e = effects[i];
    if (e !== undefined && e.type === reorderType) {
      order = e.order;
      break;
    }
  }
  if (order === undefined || order.length === 0) return [...targets];

  const rank = new Map<string, number>();
  for (let i = 0; i < order.length; i += 1) {
    const key = order[i];
    if (key !== undefined && !rank.has(key)) rank.set(key, i);
  }

  const listed: Array<{ target: LayoutTarget; rank: number; index: number }> =
    [];
  const rest: LayoutTarget[] = [];
  targets.forEach((target, index) => {
    const r = rank.get(target.key);
    if (r === undefined) rest.push(target);
    else listed.push({ target, rank: r, index });
  });
  if (listed.length === 0) return [...targets];

  listed.sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : a.index - b.index));
  return [...listed.map((entry) => entry.target), ...rest];
}

/**
 * Resolve the *effective* tooltip for a target given the draft effects: the
 * latest tooltip-affecting effect for the key wins. A `setFieldTooltip` /
 * `setColumnTooltip` yields its text; a `clearFieldTooltip` /
 * `clearColumnTooltip` yields `''` (the tooltip is forced off, even over a
 * baseline tooltip). With no draft tooltip effect it falls back to the
 * target's `baseTooltip` (config-sourced) default, so the editor can show and
 * clear a baseline tooltip. For columns this reads the header tooltip slot.
 */
export function effectiveTooltip(
  target: LayoutTarget,
  effects: readonly RuleEffect[],
): string {
  const isColumn = target.kind === 'column';
  for (let i = effects.length - 1; i >= 0; i -= 1) {
    const e = effects[i];
    if (e === undefined) continue;
    if (!isColumn) {
      if (e.type === 'setFieldTooltip' && e.key === target.key) {
        return e.tooltip;
      }
      if (e.type === 'clearFieldTooltip' && e.key === target.key) {
        return '';
      }
    } else {
      if (
        e.type === 'setColumnTooltip' &&
        e.key === target.key &&
        (e.target ?? 'header') === 'header'
      ) {
        return e.tooltip;
      }
      if (
        e.type === 'clearColumnTooltip' &&
        e.key === target.key &&
        (e.target ?? 'header') === 'header'
      ) {
        return '';
      }
    }
  }
  return target.baseTooltip ?? '';
}

/**
 * Resolve the *effective* placeholder for a placeholder-bearing target — a
 * field (`setFieldPlaceholder`), a toolbar filter (`setFilterPlaceholder`) or
 * the search box (`setSearchPlaceholder`, a singleton, so it ignores `key`).
 * The latest matching effect wins; otherwise the target's `basePlaceholder`
 * default (or `''` when none).
 */
export function effectivePlaceholder(
  target: LayoutTarget,
  effects: readonly RuleEffect[],
): string {
  for (let i = effects.length - 1; i >= 0; i -= 1) {
    const e = effects[i];
    if (e === undefined) continue;
    if (target.kind === 'filter') {
      if (e.type === 'setFilterPlaceholder' && e.key === target.key) {
        return e.placeholder;
      }
    } else if (target.kind === 'search') {
      if (e.type === 'setSearchPlaceholder') return e.placeholder;
    } else if (e.type === 'setFieldPlaceholder' && e.key === target.key) {
      return e.placeholder;
    }
  }
  return target.basePlaceholder ?? '';
}

/**
 * Resolve the *effective* front-end-required state for a field target given the
 * draft effects, mirroring {@link effectivePlaceholder}. A staged
 * `requireField` for the key wins (`required: true` + its `message`, which is
 * already defaulted to {@link DEFAULT_REQUIRED_MESSAGE} at stage time);
 * otherwise it falls back to the target's `baseRequired` default (with no
 * message, since the base config owns its own required message).
 *
 * Add-only: there is no "unrequire" effect, so the only way `required` is `true`
 * here is a staged `requireField` or a baseline-required field.
 */
export function effectiveRequired(
  target: LayoutTarget,
  effects: readonly RuleEffect[],
): { required: boolean; message?: string } {
  for (let i = effects.length - 1; i >= 0; i -= 1) {
    const e = effects[i];
    if (e !== undefined && e.type === 'requireField' && e.key === target.key) {
      return {
        required: true,
        ...(e.message !== undefined ? { message: e.message } : {}),
      };
    }
  }
  return { required: target.baseRequired ?? false };
}

/**
 * The **runtime form-value conditions** currently staged on a field key — the
 * `setFieldCondition` effects the per-field Condition panel lists (and whose
 * Trash button removes).
 *
 * Unlike the other readers this returns a *list*, because the arm is
 * append-only: several conditions may target one field and apply in effect
 * order (last matching wins). `index` is the position among **that key's**
 * conditions (0-based, in `effects` array order) — exactly what a
 * `removeCondition` edit expects, so a row's Trash button can pass its own
 * index straight through.
 *
 * Takes only `Pick<LayoutTarget, 'key'>` so the panel (and tests) can ask about
 * a bare key without assembling a full target.
 */
export function effectiveConditions(
  target: Pick<LayoutTarget, 'key'>,
  effects: readonly RuleEffect[],
): Array<{ index: number; condition: RuleFieldCondition }> {
  const staged: Array<{ index: number; condition: RuleFieldCondition }> = [];
  effects.forEach((e) => {
    if (e.type !== 'setFieldCondition' || e.key !== target.key) return;
    staged.push({
      index: staged.length,
      condition: {
        when: e.when,
        operator: e.operator,
        // Keep `value` absent when the effect omits it, so the round-trip
        // (read → re-stage) is byte-identical.
        ...(e.value !== undefined ? { value: e.value } : {}),
        effect: e.effect,
      },
    });
  });
  return staged;
}

/**
 * Resolve the *effective* helper text for a field target: the latest
 * `setFieldHelperText` effect for its key, or the target's `baseHelperText`
 * default (or `''` when none).
 */
export function effectiveHelperText(
  target: LayoutTarget,
  effects: readonly RuleEffect[],
): string {
  for (let i = effects.length - 1; i >= 0; i -= 1) {
    const e = effects[i];
    if (
      e !== undefined &&
      e.type === 'setFieldHelperText' &&
      e.key === target.key
    ) {
      return e.helperText;
    }
  }
  return target.baseHelperText ?? '';
}

/**
 * Resolve the *effective* component title: the latest `setTitle` effect's
 * value, or the supplied base title (the published-or-base label) when no
 * `setTitle` is in effect.
 */
export function effectiveTitle(
  baseTitle: string,
  effects: readonly RuleEffect[],
): string {
  for (let i = effects.length - 1; i >= 0; i -= 1) {
    const e = effects[i];
    if (e !== undefined && e.type === 'setTitle') {
      return e.title;
    }
  }
  return baseTitle;
}

/**
 * Resolve the *effective* label for an action button: the latest
 * `setActionLabel` for its key, or the target's `baseLabel`.
 */
export function effectiveActionLabel(
  key: string,
  target: LayoutTarget,
  effects: readonly RuleEffect[],
): string {
  for (let i = effects.length - 1; i >= 0; i -= 1) {
    const e = effects[i];
    if (e !== undefined && e.type === 'setActionLabel' && e.action === key) {
      return e.label;
    }
  }
  return target.baseLabel;
}
